import React, { forwardRef } from 'react';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import {
  DataGrid as MuiDataGrid,
  DataGridProps as MuiDataGridProps,
  GridRenderCellParams,
  GridValidRowModel,
} from '@mui/x-data-grid';
import styled from '@mui/material/styles/styled';
import { Checkbox } from '@im/ui-lib/Checkbox';
import { useLocalization } from '@im/react-localization';
import { isEmbedded } from '../../util';
import { useSearchParams } from 'react-router-dom';
import { HEADER_HEIGHT } from '../HeaderX4C';

export const GridCheckbox = forwardRef<HTMLInputElement>((props, ref) => {
  return <Checkbox {...props} inputRef={ref} />;
});

const DATA_GRID_TOP = isEmbedded() ? '0px' : HEADER_HEIGHT;

type DataGridProps<T extends GridValidRowModel = any> = MuiDataGridProps<T> & {
  sortIcon?: boolean;
  stickyHeader?: boolean;
};

interface DataGridComponent {
  <R extends GridValidRowModel = any>(
    props: DataGridProps<R> & React.RefAttributes<HTMLDivElement>
  ): JSX.Element;
  propTypes?: any;
}

export function RenderDateCell(params: Readonly<GridRenderCellParams>) {
  const { formatDate } = useLocalization();
  return <Typography variant="body2">{formatDate(params.value)}</Typography>;
}

export function RenderCellWithTooltip(params: Readonly<GridRenderCellParams>) {
  return (
    <Tooltip title={params.formattedValue ?? ''}>
      <span
        className="table-cell-trucate"
        style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        {params.formattedValue}
      </span>
    </Tooltip>
  );
}
export function renderJustification(param: GridRenderCellParams) {
  return (
    <Tooltip title={param.row.comments.autoDenialJustification ?? ''}>
      <span
        className="table-cell-trucate"
        style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        {param.row.comments.autoDenialJustification}
      </span>
    </Tooltip>
  );
}
export function renderCommentCell(param: GridRenderCellParams) {
  return (
    <Tooltip title={param.row.comments.comment ?? ''}>
      <span
        className="table-cell-trucate"
        style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        {param.row.comments.comment}
      </span>
    </Tooltip>
  );
}

export function RenderDescriptor2(item: Readonly<GridRenderCellParams>) {
  const descriptor2 = item.row.vendorName;
  return (
    <Tooltip title={descriptor2}>
      <span>{descriptor2}</span>
    </Tooltip>
  );
}
export function RenderDescriptor3(item: Readonly<GridRenderCellParams>) {
  const descriptor3 = '';

  return (
    <Tooltip title={descriptor3}>
      <span>{descriptor3}</span>
    </Tooltip>
  );
}

export function RenderBusinessline(item: Readonly<GridRenderCellParams>) {
  const bcn = item.row.branchCustomerNumber ?? '';
  const vnum = item.row.vendorNumber ?? '';
  return (
    <Tooltip
      title={`${bcn !== null ? 'BCN' : ''}
        ${vnum && vnum !== null ? '-Vendor' : ''} ${' '}${bcn}
        ${(vnum && vnum !== null && '-') ?? ''}
        ${vnum}
       `}
    >
      <span>
        {bcn !== null ? 'BCN' : ''}
        {vnum !== null ? '-Vendor' : ''} {bcn}
        {(vnum && vnum !== null && '-') ?? ''}
        {vnum}
      </span>
    </Tooltip>
  );
}

export function RenderLocalDateCell(params: Readonly<GridRenderCellParams>) {
  const [searchParams] = useSearchParams();
  const countryCode = searchParams.get('countryCode')?.toUpperCase() ?? 'US';
  const dateobj = new Date(params.value);
  const { formatDate } = useLocalization();
  const timezonemin = new Date().getTimezoneOffset();
  const utcDateTime = dateobj.getTime() - timezonemin * 60 * 1000;
  const utcDate = new Date(utcDateTime);
  const options = { timeZone: 'America/Los_Angeles' };
  return (
    <Typography variant="body2">
      {countryCode === 'US'
        ? formatDate(
            new Date(utcDate.toLocaleString('en-US', options))
          ).toString()
        : formatDate(utcDate).toString()}
    </Typography>
  );
}
export function RenderCurrencyCell(params: Readonly<GridRenderCellParams>) {
  const { formatCurrency } = useLocalization();
  return (
    <Typography variant="body2">{formatCurrency(params.value)}</Typography>
  );
}

export const DataGrid = styled(MuiDataGrid)<DataGridProps>((props) => ({
  '& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeader--sorted) .MuiDataGrid-sortIcon':
    {
      opacity: '0.4 !important',
    },
  '& .MuiDataGrid-iconButtonContainer': props.sortIcon
    ? {
        visibility: 'visible !important',
        width: 'auto !important',
      }
    : undefined,
  '&.MuiDataGrid-root': {
    border: 'none',
    fontSize: '14px',
  },
  '.MuiDataGrid-cell': {
    outline: 'transparent !important',
    padding: '0 20px',
  },
  '.MuiDataGrid-columnHeader': {
    padding: '0 20px',
  },
  '.MuiDataGrid-columnHeaderTitleContainer': {
    fontWeight: '600',
    lineHeight: '1rem',
    padding: '0px',
    textOverflow: 'unset',
    whiteSpace: 'break-spaces',
  },
  '.MuiDataGrid-columnHeaders': {
    background: '#F8F8F8;',
    borderBottom: '2px solid #0077D4;',
    fontSize: '16px',
    position: !props.loading && props.stickyHeader ? 'sticky' : undefined,
    top: !props.loading && props.stickyHeader ? DATA_GRID_TOP : 0,
    whiteSpace: 'nowrap',
    zIndex: 1,
  },
  '.MuiDataGrid-columnSeparator': {
    display: 'none',
  },
  '.MuiDataGrid-main': {
    overflow: !props.loading && props.stickyHeader ? 'unset' : undefined,
  },
  '.MuiDataGrid-row': {
    borderBottom: '2px solid #ddd;',
  },
  '.MuiDataGrid-virtualScroller': {
    height: props.loading ? '10rem !important' : 'auto',
    marginTop: props.stickyHeader ? '0px !important' : undefined,
  },
  '.MuiSvgIcon-root': {
    color: '#0077d4',
  },
  '.table_cell_hover': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
})) as DataGridComponent;
